import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { usePipedriveStore, usePopupStore, useSaleCompStore, useViewSettingsStore } from "../..";
import useNavigateToSaleComp from "../../hooks/useNavigateToSaleComp";
import { saleCompDealProfileToString, saleCompPropertyTypeToString, saleCompSourceToString } from "../../utils/enumUtils";
import { formatDate, formatDateTime, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import FieldRow from "../FieldRow";
import ExpandIcon from "../icons/ExpandIcon";
import SaleCompIcon from "../icons/SaleCompIcon";
import MapPopupWithFlyTo from "../MapPopupWithFlyTo";
import popupStyles from "../Popup.module.css";

function SaleCompPopup({ id }: ISaleCompPopupProps) {
    const navigateToSaleComp = useNavigateToSaleComp();
    const saleCompStore = useSaleCompStore();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const viewSettingsStore = useViewSettingsStore();
    const saleComp = useMemo(() => computed(() => saleCompStore.saleComps.find(sc => sc.id === id)), [id]).get();
    const buyerPipedriveOrganization = useMemo(() => computed(() => saleComp && pipedriveStore.organizations.find(o => o.id === saleComp.pipedriveBuyerId)), [saleComp]).get();
    const sellerPipedriveOrganization = useMemo(() => computed(() => saleComp && pipedriveStore.organizations.find(o => o.id === saleComp.pipedriveSellerId)), [saleComp]).get();
    const brokeragePipedriveOrganization = useMemo(() => computed(() => saleComp && pipedriveStore.organizations.find(o => o.id === saleComp.pipedriveBrokerageId)), [saleComp]).get();

    if (saleCompStore.isSaleCompsLoading.fetching)
        return null;

    if (!saleComp)
        return null;

    return (
        <MapPopupWithFlyTo
            longitude={saleComp.centerPoint.longitude}
            latitude={saleComp.centerPoint.latitude}
            closeButton={true}
            closeOnClick={false}
            onClose={() => popupStore.close()}
            anchor={viewSettingsStore.isPopupExpanded ? "left" : "bottom"}
            offset={20}
        >
            <div className={popupStyles.header}>
                <SaleCompIcon color="white" />
                <div className={popupStyles.titleClickable} onClick={() => navigateToSaleComp(id, true)}>
                    <div>{saleComp.address}</div>
                    {!!saleComp.saleDate && <span className={popupStyles.headerDescription}>SOLD: {formatDate(saleComp.saleDate)}</span>}
                </div>
            </div>
            <div className={popupStyles.table}>
                <div className={popupStyles.tableItem}>
                    <div>Sale Price</div>
                    <div>{!!saleComp.salePrice && formatPrice(saleComp.salePrice)}</div>
                </div>
                <div className={popupStyles.tableItem}>
                    <div>Building $/PSF</div>
                    <div>{!!saleComp.buildingPerSqFt && formatPrice(saleComp.buildingPerSqFt)}</div>
                </div>
                <div className={popupStyles.tableItem}>
                    <div>Uable $/Acre</div>
                    <div>{!!saleComp.pricePerUsableAcre && formatPrice(saleComp.pricePerUsableAcre)}</div>
                </div>
            </div>
            <div className={popupStyles.body}>
                {viewSettingsStore.isPopupExpanded && (
                    <>
                        <FieldRow label="PARCEL SIZE">
                            {!!saleComp.parcelSizeAcres && `${formatNumber(saleComp.parcelSizeAcres)} Acres`}
                        </FieldRow>
                        <FieldRow label="LAND SIZE USABLE">
                            {!!saleComp.landSizeUsable && `${formatNumber(saleComp.landSizeUsable)} Acres`}
                        </FieldRow>
                    </>
                )}
                <FieldRow label="BUILDING SIZE">
                    {!!saleComp.buildingSize && `${formatNumber(saleComp.buildingSize)} Sq-Ft`}
                </FieldRow>
                <FieldRow label="GROSS $/ACRE">
                    {!!saleComp.grossPerAcre && formatPrice(saleComp.grossPerAcre)}
                </FieldRow>
                <FieldRow label="BUYER">
                    {buyerPipedriveOrganization?.name || saleComp.buyerName}
                </FieldRow>
                <FieldRow label="SELLER">
                    {sellerPipedriveOrganization?.name || saleComp.sellerName}
                </FieldRow>
                {viewSettingsStore.isPopupExpanded && (
                    <>
                        <FieldRow label="BROKERAGE">
                            {brokeragePipedriveOrganization?.name || saleComp.brokerageName}
                        </FieldRow>
                        <FieldRow label="MARKETED">
                            {saleComp.isMarketed ? "Yes" : "No"}
                        </FieldRow>
                    </>
                )}
                <FieldRow label="ZONING">
                    {saleComp.zoning}
                </FieldRow>
                {viewSettingsStore.isPopupExpanded && (
                    <>
                        <FieldRow label="PROPERTY TYPE">
                            {!!saleComp.propertyType && saleCompPropertyTypeToString(saleComp.propertyType)}
                        </FieldRow>
                        <FieldRow label="DEAL PROFILE">
                            {!!saleComp.dealProfile && saleCompDealProfileToString(saleComp.dealProfile)}
                        </FieldRow>
                        <FieldRow label="COMP SOURCE">
                            {!!saleComp.compSource && saleCompSourceToString(saleComp.compSource)}
                        </FieldRow>
                        <FieldRow label="NOTES">
                            {saleComp.notes}
                        </FieldRow>
                    </>
                )}
                <div className={popupStyles.footer}>
                    <div className={popupStyles.modified}>
                        <span>{saleComp.modifiedByUser}</span>
                        {!!saleComp.modifiedDate && <><br /><span>{formatDateTime(saleComp.modifiedDate)}</span></>}
                    </div>
                    <ExpandIcon />
                </div>
            </div>
        </MapPopupWithFlyTo>
    );
}

export default observer(SaleCompPopup);

interface ISaleCompPopupProps {
    id: number;
}
